package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.dom.observers.ResizeObserver
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focusWithin
import com.varabyte.kobweb.silk.style.selectors.placeholder
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.css.CSSStyleDeclaration

val LSearchFieldStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(ControlHeight.MD)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(leftRight = Space.MD)
            .transitionColors()
    }
    focusWithin {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    // No visible label sibling to protect here — `label` is an aria-label on the input alone — so
    // the floor goes directly on this, the field's own outermost element.
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
}

val LSearchInputStyle = CssStyle {
    base {
        Modifier
            .flexGrow(1)
            .width(100.percent)
            .height(100.percent)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .color(colorMode.toSitePalette().textPrimary)
            .fontSize(TypeScale.BodySmall)
            .styleModifier {
                property("border", "0")
                property("outline", "0")
                property("appearance", "none")
                property("min-width", "0")
                // **A placeholder that does not fit is trimmed, never sliced.** The collapsed field
                // in the app shell is around 250px at 390, and the browser's default is to clip the
                // text at the content edge — the Wave 5 recapture of `/…/data/jobs` shows "Search
                // Lingvo Mobile or ru", cut through the middle of a glyph, which reads as a
                // rendering fault rather than as an abbreviation. An input ellipsises its own value
                // and its own placeholder from this one property.
                //
                // This is the floor, not the answer: the answer is [shorterPlaceholders], where a
                // caller hands over every sentence it would accept and the field picks the longest
                // one that fits the width it actually has. The floor stays under that choice,
                // because the shortest sentence a caller offers can still be wider than the field
                // on a narrow enough surface.
                property("text-overflow", "ellipsis")
            }
    }
    placeholder {
        Modifier.color(colorMode.toSitePalette().textMuted)
    }
}

/**
 * A search input that names what it searches, in as many words as it has room for.
 *
 * @param placeholder the sentence the field prefers, shown wherever it fits.
 * @param shorterPlaceholders the same promise in fewer words, longest first. The field shows the
 *   first entry — [placeholder] included — that fits the width it has been given, and keeps the
 *   shortest when none of them do. Empty means the field has only one sentence and shows it.
 */
@Composable
fun LSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lsearch_field.search", "Search"),
    placeholder: String = t("ds.ds.lsearch_field.search_placeholder", "Search keys and translations"),
    shorterPlaceholders: List<String> = emptyList(),
    enabled: Boolean = true,
    shortcutHint: String? = "/",
    onCommit: () -> Unit = {},
) {
    // Split into its keys rather than drawn as one cap. Today every call site passes `"/"` and the
    // two spellings agree — but `shortcutHint = "⌘K"` is one keystroke away from being written, and
    // a single `<kbd>⌘K</kbd>` announces "command K" as one word to a screen reader and draws two
    // keys as one to everybody else. `LChord` is where that decision already lives.
    val hintKeys = remember(shortcutHint) { shortcutHint?.let(::chordKeys).orEmpty() }
    val sentences = remember(placeholder, shorterPlaceholders) {
        (listOf(placeholder) + shorterPlaceholders)
            .filter(String::isNotBlank)
            .distinct()
            .ifEmpty { listOf(placeholder) }
    }
    // An id rather than a `ref`, matching how the rest of this codebase reaches a laid-out element
    // from an effect. Sequenced, because two fields on one page — the shell's and a page's own —
    // must not measure each other.
    val inputId = remember { nextSearchFieldId() }
    var shown by remember(sentences) { mutableStateOf(sentences.first()) }
    // **Measured on the field, never on the viewport.** The shell used to swap sentences at a 900px
    // media query, which is a guess about a width the field does not have: at 390 the bar's own
    // gutters, the account pill and the field's icon left it around 250px, and the *short* sentence
    // was clipped to "Search or run a comma…" on every route the re-shoot captured. A field that
    // asks its own box how wide it is cannot be wrong about it, and it re-asks whenever the box
    // changes — first paint, a window resize, and the moment the trail beside it learns a project's
    // real name and takes the width back.
    DisposableEffect(sentences, inputId) {
        val input = document.getElementById(inputId) as? HTMLInputElement
        if (input == null || sentences.size < 2) {
            onDispose { }
        } else {
            var live = true
            val choose = { if (live) shown = lSearchPlaceholderFor(input, sentences) }
            choose()
            // The placeholder is painted inside the input and changes none of its geometry, so this
            // observer cannot drive itself.
            val observer = ResizeObserver { choose() }
            observer.observe(input)
            // **The first measurement is taken in the wrong font.** The three faces this product
            // sets load with `display: swap`, so first paint is a fallback stack and the swap can
            // widen every string in the field without changing the field's own box — nothing for
            // the observer above to see. `fonts.ready` is the browser saying the measurement is
            // finally worth taking.
            val fonts = document.asDynamic().fonts
            if (fonts != null && fonts != undefined) {
                val remeasure: (dynamic) -> Unit = { choose() }
                fonts.ready.then(remeasure)
            }
            onDispose {
                live = false
                observer.disconnect()
            }
        }
    }
    Row(
        LSearchFieldStyle.toModifier()
            .then(modifier)
            .attr("data-disabled", (!enabled).toString()),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LSearchIcon(
            // Sized on both axes. `.width()` alone left height at `LIconStyle`'s 20px default and
            // drew an 18x20 glyph — the exact mismatch `Modifier.iconSize` exists to make unsayable.
            Modifier
                .iconSize(IconSize.LG)
                .color(ColorMode.current.toSitePalette().textMuted),
        )
        Input(
            type = InputType.Search,
            attrs = LSearchInputStyle.toModifier()
                .padding(leftRight = Space.SM)
                .toAttrs {
                    id(inputId)
                    value(value)
                    placeholder(shown)
                    attr("aria-label", label)
                    if (!enabled) disabled()
                    onInput { onValueChange(it.value) }
                    onKeyDown { if (it.key == "Enter") onCommit() }
                },
        )
        if (value.isEmpty()) {
            // **Above MD only.** Below it the field is the width of a phone's top bar — around
            // 130px in the app shell — and two 20px key caps ate half of it to advertise a chord no
            // phone can press. The hint is a convenience for the keyboard, so it belongs where a
            // keyboard is.
            LChord(hintKeys, Modifier.displayIfAtLeast(Breakpoint.MD))
        }
    }
}

/**
 * The longest of [candidates] that fits [input]'s content box, or the last when none of them does.
 *
 * Public because the choice is the fix, and a fix nothing can measure is a fix nobody can keep: the
 * shell's own browser test runs this against a field laid out in a real 390px CSS viewport, which is
 * the geometry the re-shoot photographed and no fixture mounted at the runner's desktop width can
 * reproduce.
 *
 * A canvas measurement rather than `scrollWidth`, because **a placeholder contributes nothing to an
 * input's scroll width** — a field whose placeholder is amputated reports exactly the geometry of one
 * whose placeholder fits, which is why this defect survived two waves of tests. `measureText` in the
 * element's own computed font is the browser's own answer to "how wide would this string be".
 *
 * @param candidates longest first, at least one. A field that is not laid out at all — `clientWidth`
 *   zero, which is what an element reports before it is in the document — keeps the first, so the
 *   sentence a caller preferred is what a reader sees if measurement never becomes possible. That is
 *   deliberately not the same test as "no room": a field squeezed to its own padding is laid out,
 *   and the honest answer there is the shortest thing the caller was willing to say.
 */
fun lSearchPlaceholderFor(input: HTMLInputElement, candidates: List<String>): String {
    val preferred = candidates.firstOrNull() ?: return ""
    if (candidates.size < 2 || input.clientWidth <= 0) return preferred
    val computed = input.ownerDocument?.defaultView?.getComputedStyle(input) ?: return preferred
    val room = input.clientWidth.toDouble() -
        cssPixels(computed.paddingLeft) -
        cssPixels(computed.paddingRight)
    return candidates.firstOrNull { runWidth(computed, it) <= room } ?: candidates.last()
}

/** One canvas for the life of the page: creating one per measurement is a node per resize frame. */
private val measuringCanvas: HTMLCanvasElement by lazy {
    document.createElement("canvas") as HTMLCanvasElement
}

private fun runWidth(computed: CSSStyleDeclaration, text: String): Double {
    val context = measuringCanvas.getContext("2d").asDynamic()
    // Chrome answers the `font` shorthand for a computed style, but it is allowed to answer blank;
    // the longhands always resolve, so they are the fallback rather than an estimate.
    val shorthand = (computed.asDynamic().font as? String).orEmpty()
    context.font = shorthand.ifBlank {
        "${computed.fontStyle} ${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}"
    }
    // `measureText` does not apply tracking, and this design system sets it on display runs.
    val tracking = cssPixels(computed.letterSpacing)
    return (context.measureText(text).width as Double) + tracking * text.length
}

private fun cssPixels(value: String): Double = value.removeSuffix("px").trim().toDoubleOrNull() ?: 0.0

private var searchFieldSequence = 0

private fun nextSearchFieldId(): String {
    searchFieldSequence += 1
    return "l-search-field-$searchFieldSequence"
}
